// Typed client for the user-facing support surface (/v1/support/*). Mirrors
// the backend envelopes verbatim (flat shapes, no `{data: ...}` wrapper).
// The admin side reuses the same Ticket/Message/TicketDetail types from here.

import { api } from '@/lib/api';

export type TicketStatus = 'open' | 'waiting_on_us' | 'waiting_on_customer' | 'closed';

// List-row shape from GET /v1/support/tickets.
export interface TicketListItem {
  id: string;
  // Short human-friendly id ("#42") — displayed everywhere so users can
  // reference a ticket by number instead of an opaque tkt_… ULID.
  number: number;
  subject: string;
  status: TicketStatus;
  last_message_at: string;
  message_count: number;
  created_at: string;
}

export interface TicketsPage {
  items: TicketListItem[];
  next_cursor: string | null;
}

// POST /support/tickets returns the full thread envelope (same shape as
// GET detail), NOT a flat ticket — re-using TicketDetail so callers
// navigate via `created.ticket.id`. The original flat declaration here
// silently produced `/support/tickets/undefined` on every fresh create.
export type TicketCreated = TicketDetail;

export interface TicketMessage {
  id: string;
  body: string;
  is_staff: boolean;
  author_email: string;
  created_at: string;
}

// GET detail envelope. Same shape on both the user and admin sides.
export interface TicketDetail {
  ticket: {
    id: string;
    number: number;
    subject: string;
    status: TicketStatus;
    created_at: string;
    last_message_at: string;
  };
  messages: TicketMessage[];
}

export interface ListTicketsParams {
  cursor?: string;
  limit?: number;
}

export async function createTicket(body: {
  subject: string;
  body: string;
}): Promise<TicketCreated> {
  const resp = await api.post<TicketCreated>('/support/tickets', body);
  return resp.data;
}

export async function listTickets(params: ListTicketsParams = {}): Promise<TicketsPage> {
  const resp = await api.get<TicketsPage>('/support/tickets', {
    params: {
      cursor: params.cursor || undefined,
      limit: params.limit ?? 20,
    },
  });
  return resp.data;
}

export async function getTicket(id: string): Promise<TicketDetail> {
  const resp = await api.get<TicketDetail>(`/support/tickets/${id}`);
  return resp.data;
}

export async function replyToTicket(id: string, body: string): Promise<void> {
  await api.post(`/support/tickets/${id}/messages`, { body });
}
